/**
 * @file: ChatRoomDetailView.tsx
 *
 * Chat room detail (members grid + settings) — navigation-agnostic. Takes the
 * resolved conversation `handle` (a `baked` room or a `bare` phantom private
 * chat), calls `onAddMembers` to launch the contact picker (the host decides
 * push vs overlay); `embedded`/`onClose` render the desktop info-column
 * PaneHeader. State + actions live in `useRoomDetail`. Embeddable (mobile
 * screen + desktop info column).
 */

import { Typography } from '@euler/app/components/typography';
import {
  ChatConversationHandle,
  roomTitle,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { usePresenceRefresh } from '@euler/app/flows/chat/lib/session/hooks/usePresence';
import { ChatContactPickerParams } from '@euler/app/flows/chat/lib/types';
import { PaneHeader } from '@euler/components/PaneHeader';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRoomDetail } from '../functions/useRoomDetail';
import {
  OrderLinkTarget,
  orderLinkTarget,
  useRoomOrder,
} from '../functions/useRoomOrder';
import { AddButton, MemberAvatar, RemoveButton } from './MemberGrid';
import { OrderLinkButton } from './OrderLinkButton';
import { RemoveMembersPanel } from './RemoveMembersPanel';
import { RoomOrderCard } from './RoomOrderCard';
import { LinkRow, SettingRow, SettingSeparator } from './SettingRow';

export const ChatRoomDetailView: FC<{
  handle: ChatConversationHandle;
  /** Desktop info column: render a PaneHeader (mobile uses the nav header). */
  embedded?: boolean;
  onAddMembers: (params: ChatContactPickerParams) => void;
  /** Whether the "add members" tile shows (off where there's no picker
   * surface, e.g. the floating chat panel). */
  canAddMembers?: boolean;
  /** Embedded PaneHeader back button (an in-pane sub-page host, e.g. floating
   * chat). */
  onBack?: () => void;
  /** Embedded PaneHeader "open order" link visibility (the host hides it when
   * the order is already the screen behind the panel). Default true. */
  showOrderLink?: boolean;
  onClose?: () => void;
  /** Called after a successful leave/dismiss — host pops back / closes pane. */
  onExit?: () => void;
  /** Called after a successful self-join — host opens the conversation. */
  onJoined?: () => void;
  /** Reports the order-room's header-link target so the host (which owns the
   * mobile nav header) can render the "open order" link. Null when not an order
   * room or the order can't be resolved. (Desktop renders the link itself in
   * the PaneHeader.) */
  onOrderLink?: (target: OrderLinkTarget | null) => void;
}> = memo(
  ({
    handle,
    embedded,
    onAddMembers,
    canAddMembers = true,
    onBack,
    showOrderLink = true,
    onClose,
    onExit,
    onJoined,
    onOrderLink,
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const {
      room,
      loading,
      isPhantom,
      isGroup,
      canToggleSettings,
      muted,
      onToggleMute,
      pinned,
      onTogglePin,
      canDismiss,
      canRename,
      onRename,
      removableMembers,
      onRemoveMembers,
      onLeave,
      onDismiss,
      dismissed,
      onDeleteThread,
      busy,
      onAddPress,
      canJoin,
      onJoin,
    } = useRoomDetail(handle, onAddMembers, onExit, onJoined);

    // The "remove members" sheet (owner-only; the minus tile opens it).
    const [removeVisible, setRemoveVisible] = useState(false);
    const canRemoveMembers =
      canAddMembers && !dismissed && removableMembers.length > 0;

    // Order-internal rooms: resolve the order once (shared by the order card and
    // the header link), then hand the link target to the host's nav header.
    const { order } = useRoomOrder(room?.orderNo ?? null);
    const orderTarget = useMemo(() => orderLinkTarget(order), [order]);
    useEffect(() => {
      onOrderLink?.(orderTarget);
    }, [onOrderLink, orderTarget]);

    // Seed the online dots for the member grid (live `presence` events keep
    // them current after this snapshot).
    usePresenceRefresh(room?.members ?? []);

    if (loading) {
      return (
        <View
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <ActivityIndicator />
        </View>
      );
    }

    if (!room) {
      return (
        <View
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography.Text size={rpx(16)} color="#999">
            聊天室不存在
          </Typography.Text>
        </View>
      );
    }

    return (
      <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
        {embedded ? (
          <PaneHeader
            title="聊天详情"
            align="center"
            onBack={onBack}
            right={
              orderTarget && showOrderLink ? (
                <OrderLinkButton target={orderTarget} />
              ) : undefined
            }
            onClose={onClose}
          />
        ) : null}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(20),
          }}
        >
          {/* Order details (order-internal rooms only). Renders immediately
              with the plate from the room snapshot + placeholders, so it
              doesn't shift when the order resolves. */}
          {room.orderNo ? (
            <RoomOrderCard
              order={order}
              licensePlateNo={room.licensePlateNo ?? null}
            />
          ) : null}

          {/* Members grid. Bottom padding is small because each tile already
            carries a 12rpx bottom margin (for multi-row wrap) — 12+4 matches
            the 16 top padding without a trailing gap. */}
          <View
            css={`
              background-color: #fff;
              padding: ${rpx(16)}px ${rpx(16)}px ${rpx(4)}px;
              margin-bottom: ${rpx(8)}px;
            `}
          >
            <View
              css={`
                flex-direction: row;
                flex-wrap: wrap;
              `}
            >
              {room.members.map(m => (
                <MemberAvatar key={m.id} member={m} />
              ))}
              {/* No inviting into a dismissed room, where there's no picker
                  surface (the floating chat panel), or when the viewer isn't a
                  member yet (they join first). */}
              {dismissed || !canAddMembers || canJoin ? null : (
                <AddButton onPress={onAddPress} />
              )}
              {/* Owner-only "remove members" entry → multi-select sheet. */}
              {canRemoveMembers ? (
                <RemoveButton onPress={() => setRemoveVisible(true)} />
              ) : null}
            </View>
          </View>

          {/* Non-member viewing a same-store group: join CTA below the member
              list. On join the detail reloads as a member view. */}
          {canJoin ? (
            <View
              css={`
                background-color: #fff;
                padding: ${rpx(12)}px ${rpx(16)}px;
                margin-bottom: ${rpx(8)}px;
              `}
            >
              <TouchableOpacity
                onPress={onJoin}
                disabled={busy}
                activeOpacity={0.8}
                style={{
                  paddingVertical: rpx(12),
                  borderRadius: rpx(8),
                  alignItems: 'center',
                  backgroundColor: '#3b82f6',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Typography.Text size={rpx(15)} color="#fff">
                    加入群聊
                  </Typography.Text>
                )}
              </TouchableOpacity>
            </View>
          ) : null}

          {/* Settings — hidden once the room is dismissed (read-only) or when
              the viewer isn't a member yet (they see only the join CTA). */}
          {dismissed || canJoin ? null : (
            <>
              {/* Group name (owner of a custom group can rename it). */}
              {canRename ? (
                <View
                  css={`
                    background-color: #fff;
                    margin-bottom: ${rpx(8)}px;
                  `}
                >
                  <LinkRow
                    label="群聊名称"
                    value={room.title || roomTitle(handle)}
                    onPress={onRename}
                    disabled={busy}
                  />
                </View>
              ) : null}
              <View
                css={`
                  background-color: #fff;
                  margin-bottom: ${rpx(8)}px;
                `}
              >
                <SettingRow
                  label="消息免打扰"
                  value={muted}
                  onValueChange={onToggleMute}
                  disabled={!canToggleSettings}
                />
                <SettingSeparator />
                <SettingRow
                  label="置顶聊天"
                  value={pinned}
                  onValueChange={onTogglePin}
                  disabled={!canToggleSettings}
                />
              </View>
              {!canToggleSettings && (isPhantom || isGroup) ? (
                <View
                  css={`
                    padding: ${rpx(12)}px ${rpx(16)}px;
                  `}
                >
                  <Typography.Text size={rpx(11)} color="#9ca3af">
                    发送第一条消息后即可使用以上设置
                  </Typography.Text>
                </View>
              ) : null}
            </>
          )}

          {/* Danger zone: a dismissed room can only be deleted; otherwise the
              owner dismisses and members leave. */}
          {dismissed ? (
            <View
              css={`
                background-color: #fff;
                margin-top: ${rpx(8)}px;
              `}
            >
              <DangerRow
                label="删除聊天"
                onPress={onDeleteThread}
                disabled={busy}
              />
            </View>
          ) : isGroup && !canJoin ? (
            <View
              css={`
                background-color: #fff;
                margin-top: ${rpx(8)}px;
              `}
            >
              {canDismiss ? (
                <DangerRow
                  label="解散群聊"
                  onPress={onDismiss}
                  disabled={busy}
                />
              ) : (
                <DangerRow label="退出群聊" onPress={onLeave} disabled={busy} />
              )}
            </View>
          ) : null}
        </ScrollView>

        <RemoveMembersPanel
          visible={removeVisible}
          members={removableMembers}
          busy={busy}
          onClose={() => setRemoveVisible(false)}
          onConfirm={ids => {
            setRemoveVisible(false);
            onRemoveMembers(ids);
          }}
        />
      </View>
    );
  },
);

/** A centered red action row (leave / dismiss), disabled while in flight. */
const DangerRow: FC<{
  label: string;
  onPress: () => void;
  disabled?: boolean;
}> = memo(({ label, onPress, disabled }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={{
        paddingVertical: rpx(14),
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography.Text size={rpx(15)} color="#dc2626">
        {label}
      </Typography.Text>
    </TouchableOpacity>
  );
});
